import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, Banknote, HeartPulse, Landmark, Factory, Cpu,
  Store, GraduationCap, ScrollText, Wrench, Rocket, ArrowRight, Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SeoHead from "@/components/seo/SeoHead";
import { breadcrumb, itemList } from "@/components/seo/schemas";

// ─── Content: swap these for real anonymized customer stories over time ───
// Every field is user-facing. Keep quotes 1–2 lines. Company names deliberately absent.
const FEATURED = [
  {
    id: "sf-1",
    industry: "SaaS",
    industryTag: "SaaS · Series B",
    IndustryIcon: Building2,
    metric: "42%",
    metricSub: "higher outbound reply rate",
    quote:
      "Bulk mobile discovery cut prospecting time in half. Reps hit quota every week without babysitting the CRM.",
    role: "Head of Sales",
    size: "51–200 employees",
    feature: "Bulk Mobile Discovery",
  },
  {
    id: "ft-1",
    industry: "Fintech",
    industryTag: "Fintech · Growth stage",
    IndustryIcon: Banknote,
    metric: "3.1x",
    metricSub: "meetings booked per SDR / week",
    quote:
      "The ICP fit score means our SDRs stop chasing bad-fit leads. We book more, and the ones we book actually close.",
    role: "VP of Revenue",
    size: "201–1,000 employees",
    feature: "ICP Fit Scoring",
  },
  {
    id: "mt-1",
    industry: "MarTech",
    industryTag: "MarTech · Agency",
    IndustryIcon: Wrench,
    metric: "68%",
    metricSub: "email deliverability lift",
    quote:
      "The verified-email cascade dropped our bounce rate from 22% to under 3%. Outbound is finally reliable again.",
    role: "Marketing Operations Lead",
    size: "1–50 employees",
    feature: "Bulk Email Discovery",
  },
];

const GRID = [
  {
    id: "ht-1",
    industry: "HealthTech",
    IndustryIcon: HeartPulse,
    metric: "35%",
    metricSub: "faster time-to-first-meeting",
    quote:
      "Hiring signals show us who's about to expand. We're first in the inbox before the RFP even exists.",
    role: "Founding AE",
    size: "1–50 employees",
    feature: "Hiring Signals",
  },
  {
    id: "fs-1",
    industry: "Financial Services",
    IndustryIcon: Landmark,
    metric: "58%",
    metricSub: "of pipeline sourced from LeadIQ",
    quote:
      "Funding intel timing is the unlock. When a portco raises, we're the first vendor pitching the new CFO.",
    role: "Director of Sales",
    size: "201–1,000 employees",
    feature: "Funding Intel",
  },
  {
    id: "mfg-1",
    industry: "Manufacturing",
    IndustryIcon: Factory,
    metric: "2.4x",
    metricSub: "connect rate on cold dials",
    quote:
      "We used to buy stale spreadsheets. Now every mobile number is verified before a rep touches it.",
    role: "Regional Sales Manager",
    size: "1,000+ employees",
    feature: "Bulk Mobile Discovery",
  },
  {
    id: "dt-1",
    industry: "Deep Tech",
    IndustryIcon: Cpu,
    metric: "5 hrs",
    metricSub: "saved per rep, per week",
    quote:
      "The AI-drafted opener paired with each lead's tech stack lands 4 out of 5 times. Reps read, edit, send.",
    role: "SDR Team Lead",
    size: "51–200 employees",
    feature: "AI Outreach",
  },
  {
    id: "ec-1",
    industry: "E-commerce",
    IndustryIcon: Store,
    metric: "$1.2M",
    metricSub: "pipeline generated in Q1",
    quote:
      "One CSV upload gave us mobile, email, tech stack and buying committee — no toggling between six tools.",
    role: "Head of Growth",
    size: "51–200 employees",
    feature: "Bulk Mobile Discovery",
  },
  {
    id: "ed-1",
    industry: "EdTech",
    IndustryIcon: GraduationCap,
    metric: "89%",
    metricSub: "email deliverability rate",
    quote:
      "Every email is validated end-to-end. Our sender reputation stopped bleeding within the first week.",
    role: "Marketing Manager",
    size: "1–50 employees",
    feature: "Bulk Email Discovery",
  },
  {
    id: "rt-1",
    industry: "RegTech",
    IndustryIcon: ScrollText,
    metric: "4x",
    metricSub: "increase in qualified demos",
    quote:
      "The ICP score is the difference between prospecting and guessing. Guessing is dead here.",
    role: "Head of Growth",
    size: "51–200 employees",
    feature: "ICP Fit Scoring",
  },
];

const ALL_CASES = [...FEATURED, ...GRID];

const INDUSTRIES = ["All", ...Array.from(new Set(ALL_CASES.map((c) => c.industry)))];
const SIZES = ["All", "1–50 employees", "51–200 employees", "201–1,000 employees", "1,000+ employees"];
const FEATURES = [
  "All",
  "Bulk Mobile Discovery",
  "Bulk Email Discovery",
  "ICP Fit Scoring",
  "AI Outreach",
  "Hiring Signals",
  "Funding Intel",
];

// ─── Small building blocks ───
const FeatureTag = ({ label }) => (
  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widestx font-mono font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm">
    {label}
  </span>
);

const IndustryChip = ({ Icon, tag }) => (
  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widestx font-mono text-ink-500">
    <Icon className="w-3.5 h-3.5 text-ink-700" />
    <span>{tag}</span>
  </div>
);

const FilterPill = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-[10px] uppercase tracking-widestx font-semibold px-3 py-1.5 rounded-sm border transition-colors whitespace-nowrap ${
      active
        ? "bg-ink-900 text-white border-ink-900"
        : "bg-white text-ink-600 border-ink-100 hover:border-ink-400 hover:text-ink-900"
    }`}
  >
    {label}
  </button>
);

const FeaturedCard = ({ c }) => (
  <article
    data-testid={`featured-card-${c.id}`}
    className="group bg-white border border-ink-100 rounded-sm p-6 flex flex-col gap-4 hover:border-ink-900 transition-colors"
  >
    <IndustryChip Icon={c.IndustryIcon} tag={c.industryTag} />
    <div>
      <div className="font-display text-5xl lg:text-6xl leading-none font-extrabold text-ink-900">
        {c.metric}
      </div>
      <div className="text-sm text-ink-500 mt-2">{c.metricSub}</div>
    </div>
    <p className="text-[13px] leading-relaxed text-ink-700 border-l-2 border-emerald-500 pl-3">
      "{c.quote}"
    </p>
    <div className="mt-auto pt-3 border-t border-ink-100 flex items-center justify-between gap-2">
      <div className="text-[11px] text-ink-500 font-mono leading-tight">
        <div className="text-ink-900 font-semibold">{c.role}</div>
        <div>{c.size}</div>
      </div>
      <FeatureTag label={c.feature} />
    </div>
  </article>
);

const GridCard = ({ c }) => (
  <article
    data-testid={`grid-card-${c.id}`}
    className="bg-white border border-ink-100 rounded-sm p-5 flex flex-col gap-3 hover:border-ink-900 transition-colors"
  >
    <IndustryChip Icon={c.IndustryIcon} tag={c.industry} />
    <div>
      <div className="font-display text-4xl leading-none font-extrabold text-ink-900">
        {c.metric}
      </div>
      <div className="text-[12px] text-ink-500 mt-1.5">{c.metricSub}</div>
    </div>
    <p className="text-[12px] leading-relaxed text-ink-700">"{c.quote}"</p>
    <div className="mt-auto pt-3 border-t border-ink-100 flex items-center justify-between gap-2">
      <div className="text-[10px] text-ink-500 font-mono leading-tight">
        <div className="text-ink-900 font-semibold">{c.role}</div>
        <div>{c.size}</div>
      </div>
      <FeatureTag label={c.feature} />
    </div>
  </article>
);

// ─── Page ───
export default function CaseStudies() {
  const [industry, setIndustry] = useState("All");
  const [size, setSize] = useState("All");
  const [feature, setFeature] = useState("All");

  const filtered = useMemo(() => {
    return GRID.filter(
      (c) =>
        (industry === "All" || c.industry === industry) &&
        (size === "All" || c.size === size) &&
        (feature === "All" || c.feature === feature)
    );
  }, [industry, size, feature]);

  const clear = () => {
    setIndustry("All");
    setSize("All");
    setFeature("All");
  };
  const hasFilter = industry !== "All" || size !== "All" || feature !== "All";

  return (
    <div className="min-h-screen bg-canvas text-ink-900">
      <SeoHead
        title="Case Studies — LeadIQ Pro"
        description="Real B2B sales teams using LeadIQ Pro: SaaS, Fintech, Agencies and more. See how our AI waterfall enrichment + ICP fit scoring moved revenue metrics."
        path="/case-studies"
        jsonLd={[
          breadcrumb([
            { name: "Home", url: "/" },
            { name: "Case Studies", url: "/case-studies" },
          ]),
          itemList({
            name: "LeadIQ Pro — Case Studies",
            description: "How B2B sales teams across industries use LeadIQ Pro to enrich leads, score fit, and book meetings.",
            path: "/case-studies",
            items: FEATURED.map((c) => ({
              name: `${c.industryTag} — ${c.metric}`,
              description: c.summary || c.quote || `${c.industryTag} customer saw ${c.metric} after switching to LeadIQ Pro.`,
              url: "/case-studies",
            })),
          }),
        ]}
      />
      {/* Header — matches Landing page style */}
      <header className="border-b border-ink-100 bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="cs-brand-home">
            <img
              src="/logo-128.png"
              alt="LeadIQ Pro"
              width="40"
              height="40"
              className="w-10 h-10 shrink-0"
              data-testid="cs-brand-logo"
            />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-base tracking-tight text-ink-900">
                LeadIQ Pro
              </div>
              <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                Lead Enrichment Engine
              </div>
            </div>
          </Link>
          <nav className="flex items-center gap-5 text-xs uppercase tracking-widestx font-semibold">
            <Link to="/pricing" className="text-ink-500 hover:text-ink-900" data-testid="cs-nav-pricing">
              Pricing
            </Link>
            <Link to="/login" className="text-ink-500 hover:text-ink-900" data-testid="cs-nav-login">
              Sign in
            </Link>
            <Link
              to="/register"
              className="bg-ink-900 text-white px-3.5 py-1.5 rounded-sm hover:bg-ink-700"
              data-testid="cs-nav-signup"
            >
              Start free
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Hero */}
        <section className="mb-12">
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-3">
            Customer Outcomes
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-extrabold text-ink-900 max-w-4xl">
            Real workflows. <span className="font-light">Real numbers.</span>
          </h1>
          <p className="text-ink-500 text-base mt-4 max-w-2xl leading-relaxed">
            How B2B teams use LeadIQ Pro to shorten outbound cycles, lift connect rates and
            book more qualified meetings. Company names anonymized at customer request —
            metrics self-reported.
          </p>
        </section>

        {/* Featured */}
        <section className="mb-16">
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-4">
            Featured stories
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {FEATURED.map((c) => (
              <FeaturedCard key={c.id} c={c} />
            ))}
          </div>
        </section>

        {/* Filter bar */}
        <section className="mb-6 border-t border-b border-ink-100 py-6">
          <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widestx font-mono text-ink-500">
            <Filter className="w-3.5 h-3.5" />
            <span>Explore by</span>
            {hasFilter && (
              <button
                type="button"
                onClick={clear}
                data-testid="cs-clear-filters"
                className="ml-2 text-emerald-800 hover:underline normal-case tracking-normal"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 self-center min-w-[70px]">
                Industry
              </span>
              {INDUSTRIES.map((i) => (
                <FilterPill
                  key={i}
                  label={i}
                  active={industry === i}
                  onClick={() => setIndustry(i)}
                  testId={`filter-industry-${i.toLowerCase().replace(/\s+/g, "-")}`}
                />
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 self-center min-w-[70px]">
                Size
              </span>
              {SIZES.map((s) => (
                <FilterPill
                  key={s}
                  label={s.replace(" employees", "")}
                  active={size === s}
                  onClick={() => setSize(s)}
                  testId={`filter-size-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                />
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 self-center min-w-[70px]">
                Feature
              </span>
              {FEATURES.map((f) => (
                <FilterPill
                  key={f}
                  label={f}
                  active={feature === f}
                  onClick={() => setFeature(f)}
                  testId={`filter-feature-${f.toLowerCase().replace(/\s+/g, "-")}`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Grid */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-500">
              <span data-testid="cs-result-count">{filtered.length}</span> stor
              {filtered.length === 1 ? "y" : "ies"}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div
              data-testid="cs-empty"
              className="border border-dashed border-ink-200 rounded-sm px-6 py-16 text-center"
            >
              <div className="text-sm text-ink-500">
                No stories match those filters yet.
              </div>
              <button
                type="button"
                onClick={clear}
                className="mt-3 text-[11px] uppercase tracking-widestx font-semibold text-emerald-800 hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((c) => (
                <GridCard key={c.id} c={c} />
              ))}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="border border-ink-100 rounded-sm bg-white p-10 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-2 inline-flex items-center gap-1.5">
              <Rocket className="w-3.5 h-3.5" /> Get similar results
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-ink-900 leading-tight">
              Start enriching for free.
            </h2>
            <p className="text-sm text-ink-500 mt-2 max-w-md">
              Upload a CSV, run 25 enrichments on the Scale trial, and see the difference
              in your first outreach cycle.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/pricing" data-testid="cs-cta-pricing">
              <Button
                variant="outline"
                className="rounded-sm border-ink-200 hover:border-ink-900 text-ink-700 hover:text-ink-900 h-11 px-5 text-[11px] uppercase tracking-widestx font-semibold"
              >
                See pricing
              </Button>
            </Link>
            <Link to="/register" data-testid="cs-cta-signup">
              <Button className="rounded-sm bg-ink-900 hover:bg-ink-700 text-white h-11 px-5 text-[11px] uppercase tracking-widestx font-semibold gap-2">
                Start free <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Legal footnote */}
        <p className="text-[10px] text-ink-400 mt-8 font-mono max-w-3xl leading-relaxed">
          Company names anonymized at customer request. Metrics self-reported by
          customers of LeadIQ Pro; individual results will vary based on industry,
          motion and rep effort. LeadIQ Pro reserves the right to verify.
        </p>
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-ink-100 bg-white py-8 text-[11px] text-ink-400 font-mono">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between flex-wrap gap-3">
          <div>© {new Date().getFullYear()} LeadIQ Pro</div>
          <div className="flex items-center gap-4">
            <Link to="/pricing" className="hover:text-ink-900">Pricing</Link>
            <Link to="/legal/terms" className="hover:text-ink-900">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-ink-900">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
